import { FormEvent, useId, useState } from "react";
import { ArrowUpRight, Check, LoaderCircle, RotateCcw } from "lucide-react";

type FormState = "idle" | "loading" | "success" | "error";

type ContactResponse = {
  ok?: boolean;
  error?: string;
};

export function ContactForm() {
  const id = useId();
  const [state, setState] = useState<FormState>("idle");
  const [error, setError] = useState("");

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;

    if (!form.checkValidity()) {
      setState("error");
      setError("Проверьте имя и email.");
      form.reportValidity();
      return;
    }

    const data = new FormData(form);
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 12_000);

    setState("loading");
    setError("");

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: data.get("name"),
          email: data.get("email"),
          message: data.get("message"),
          website: data.get("website"),
        }),
        signal: controller.signal,
      });
      const result = await response.json().catch(() => ({})) as ContactResponse;

      if (!response.ok || !result.ok) {
        throw new Error(result.error || "Не удалось отправить заявку.");
      }

      form.reset();
      setState("success");
    } catch (submitError) {
      const message = submitError instanceof DOMException && submitError.name === "AbortError"
        ? "Сервер отвечает слишком долго. Напишите мне в Telegram."
        : submitError instanceof Error
          ? submitError.message
          : "Не удалось отправить заявку. Попробуйте ещё раз.";
      setState("error");
      setError(message);
    } finally {
      window.clearTimeout(timeout);
    }
  }

  if (state === "success") {
    return (
      <div className="portfolio-form portfolio-form--success" role="status" aria-live="polite">
        <span className="portfolio-form__success-icon"><Check aria-hidden="true" /></span>
        <p className="portfolio-form__kicker">Заявка уже в Telegram</p>
        <h3>Спасибо. Я скоро отвечу</h3>
        <p>Посмотрю задачу и свяжусь с вами по указанному email.</p>
        <button type="button" onClick={() => setState("idle")}>
          <RotateCcw aria-hidden="true" />
          Отправить ещё одну
        </button>
      </div>
    );
  }

  return (
    <form className="portfolio-form" onSubmit={handleSubmit} noValidate>
      <div className="portfolio-form__heading">
        <p className="portfolio-form__kicker">Короткий бриф</p>
        <h3>Расскажите, что нужно запустить</h3>
        <p>Имя и email обязательны. Описание задачи поможет ответить предметнее.</p>
      </div>

      <div className="portfolio-form__row">
        <label htmlFor={`${id}-name`}>
          <span>Ваше имя</span>
          <input
            id={`${id}-name`}
            name="name"
            type="text"
            autoComplete="name"
            minLength={2}
            maxLength={80}
            placeholder="Алексей"
            required
          />
        </label>
        <label htmlFor={`${id}-email`}>
          <span>Email для ответа</span>
          <input
            id={`${id}-email`}
            name="email"
            type="email"
            autoComplete="email"
            maxLength={160}
            placeholder="hello@company.com"
            required
          />
        </label>
      </div>

      <label htmlFor={`${id}-message`}>
        <span>О проекте <small>необязательно</small></span>
        <textarea
          id={`${id}-message`}
          name="message"
          maxLength={2000}
          rows={4}
          placeholder="Что продаём, для кого и когда нужен запуск"
        />
      </label>

      <label className="portfolio-form__trap" htmlFor={`${id}-website`} aria-hidden="true">
        <span>Ваш сайт</span>
        <input id={`${id}-website`} name="website" type="text" tabIndex={-1} autoComplete="off" />
      </label>

      {error && (
        <p className="portfolio-form__error" role="alert">
          {error}{" "}
          <a href="https://t.me/MrVivor" target="_blank" rel="noopener noreferrer">
            Написать @MrVivor
          </a>
        </p>
      )}

      <div className="portfolio-form__footer">
        <button type="submit" disabled={state === "loading"}>
          {state === "loading" ? (
            <>
              <LoaderCircle className="spin" aria-hidden="true" />
              Отправляю
            </>
          ) : (
            <>
              Отправить заявку
              <ArrowUpRight aria-hidden="true" />
            </>
          )}
        </button>
        <p>Отправляя форму, вы разрешаете использовать данные только для ответа на запрос.</p>
      </div>
    </form>
  );
}
